/*
 * Carrusel de imágenes para la sección «Imagen con texto superpuesto».
 *
 * La sección apila varias <picture> dentro del mismo .content-over-media del tema: como el tema ya coloca toda imagen
 * hija en la misma celda de la rejilla («full-start» a «full-end»), las imágenes quedan una encima de otra sin añadir
 * ni un contenedor. Este elemento solo decide cuál se ve: cambia la clase .is-selected —y, en el modo «deslizar», el
 * translateX de cada una—, enciende el enlace de la imagen que toca y lleva las flechas y los puntos, que viven
 * dentro de él.
 *
 * Sin JavaScript la sección sigue siendo la de siempre: la primera imagen ya viene con .is-selected desde el servidor
 * y el resto se quedan ocultas por CSS, así que nunca se ve una sección en blanco.
 *
 * Vídeos: una diapositiva de vídeo no se corta al cumplirse el intervalo; el carrusel pasa a la siguiente cuando el
 * vídeo termina. Quien lo reproduce y lo para es assets/background-video.js; aquí solo se le quita el bucle y se escucha
 * su final.
 *
 * Coste en reposo: cero. El avance automático solo existe mientras la sección está a la vista, la pestaña en primer
 * plano y el sistema no pide menos animación; si la clienta arrastra, toca, teclea o deja el ratón encima, manda ella.
 *
 * Deslizar: en el modo «deslizar» las imágenes siguen al dedo mientras arrastra y, al soltar, pasan a la siguiente o
 * vuelven a su sitio según lo que haya recorrido. La sección pone touch-action: pan-y en la imagen para que el
 * navegador del móvil le deje el gesto lateral al carrusel y se quede solo con el desplazamiento vertical.
 */
(function () {
  if (customElements.get('image-overlay-carousel')) return;

  var REDUCED_MOTION = matchMedia('(prefers-reduced-motion: reduce)');
  var RESUME_DELAY = 5000;
  // Píxeles de arrastre horizontal a partir de los cuales el gesto cuenta como pasar de imagen.
  var SWIPE_THRESHOLD = 40;
  // Píxeles que el dedo puede moverse antes de decidir si el gesto es del carrusel (lateral) o de la página (vertical).
  var DRAG_SLOP = 8;
  var PAUSE_EVENTS = ['keydown', 'focusin'];

  class ImageOverlayCarousel extends HTMLElement {
    connectedCallback() {
      this.media = this.closest('.content-over-media');

      if (!this.media) return;

      this.slides = Array.prototype.slice.call(this.media.querySelectorAll('.overlay-carousel__slide'));
      this.links = Array.prototype.slice.call(this.media.querySelectorAll('.overlay-carousel__link'));
      this.dots = Array.prototype.slice.call(this.querySelectorAll('[data-carousel-dot]'));

      // Con una sola imagen no hay carrusel que gobernar: se queda tal cual la pintó el servidor.
      if (this.slides.length < 2) return;

      this.effect = this.getAttribute('effect') === 'slide' ? 'slide' : 'fade';
      this.rtl = getComputedStyle(this.media).direction === 'rtl';
      this.index = 0;

      this.onClick = this.onClick.bind(this);
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onClickCapture = this.onClickCapture.bind(this);

      this.addEventListener('click', this.onClick);
      this.media.addEventListener('pointerdown', this.onPointerDown);
      this.media.addEventListener('pointermove', this.onPointerMove);
      this.media.addEventListener('pointerup', this.onPointerUp);
      this.media.addEventListener('pointercancel', this.onPointerUp);
      // Un arrastre no debe abrir el enlace de la imagen: se anula el clic que el navegador emite tras el gesto.
      this.media.addEventListener('click', this.onClickCapture, true);

      this.apply(true);
      this.setupAutoplay();

      // En el editor, abrir un bloque de imagen enseña esa imagen (y detiene el avance mientras está abierto).
      if (window.Shopify && window.Shopify.designMode) {
        this.section = this.closest('.shopify-section');

        if (this.section) {
          this.onBlockToggle = this.onBlockToggle.bind(this);
          this.section.addEventListener('shopify:block:select', this.onBlockToggle);
          this.section.addEventListener('shopify:block:deselect', this.onBlockToggle);
        }
      }
    }

    disconnectedCallback() {
      if (!this.media || this.slides == null || this.slides.length < 2) return;

      this.removeEventListener('click', this.onClick);
      this.media.removeEventListener('pointerdown', this.onPointerDown);
      this.media.removeEventListener('pointermove', this.onPointerMove);
      this.media.removeEventListener('pointerup', this.onPointerUp);
      this.media.removeEventListener('pointercancel', this.onPointerUp);
      this.media.removeEventListener('click', this.onClickCapture, true);
      this.teardownAutoplay();

      if (this.section) {
        this.section.removeEventListener('shopify:block:select', this.onBlockToggle);
        this.section.removeEventListener('shopify:block:deselect', this.onBlockToggle);
      }
    }

    /* ── Navegación ─────────────────────────────────────────────────────────────────────────────────────────── */

    select(index) {
      var count = this.slides.length;
      var next = ((index % count) + count) % count;

      if (next === this.index) return;

      this.index = next;
      this.apply(false);
    }

    next() {
      this.select(this.index + 1);
    }

    previous() {
      this.select(this.index - 1);
    }

    /* Posición de una imagen respecto a la que se ve: 0 es la actual, 1 la siguiente, -1 la anterior. */
    offsetOf(i) {
      var count = this.slides.length;
      var offset = i - this.index;

      // Se va siempre por el camino corto, para que del final al principio no desfilen todas las imágenes.
      if (offset > count / 2) offset -= count;
      if (offset < count / -2) offset += count;

      return offset;
    }

    /* La primera imagen la anima el tema al entrar en pantalla y deja estilos en línea; en cuanto el carrusel se mueve
       por primera vez se cancela esa animación y se limpia lo que dejó, o esa imagen se quedaría encima de las demás
       para siempre. */
    clean() {
      if (this.cleaned) return;

      this.cleaned = true;

      this.slides.forEach((slide) => {
        if (slide.getAnimations) slide.getAnimations().forEach((animation) => animation.cancel());

        slide.style.opacity = '';
        slide.style.transform = '';
        slide.style.scale = '';
      });
    }

    /* Único sitio donde se toca el DOM: reparte .is-selected, desplazamientos, enlaces y puntos. */
    apply(initial) {
      var direction = this.rtl ? -1 : 1;

      if (!initial) this.clean();

      this.slides.forEach((slide, i) => {
        var offset = this.offsetOf(i);

        slide.classList.toggle('is-selected', i === this.index);

        if (this.effect === 'slide') {
          var previous = parseFloat(slide.dataset.offset);
          // Al saltar más de una posición la imagen está fuera de la vista: se recoloca sin animar para que no cruce
          // la pantalla de lado a lado.
          var jumps = initial || isNaN(previous) || Math.abs(offset - previous) > 1;

          if (jumps) slide.style.transition = 'none';
          slide.style.transform = 'translateX(' + offset * direction * 100 + '%)';
          if (jumps) {
            void slide.offsetWidth;
            slide.style.transition = '';
          }

          slide.dataset.offset = offset;
        }
      });

      // Los enlaces van por número de imagen, porque solo se pintan los de las imágenes que tienen destino.
      this.links.forEach((link) => link.classList.toggle('is-selected', parseInt(link.dataset.slide, 10) === this.index));
      this.dots.forEach((dot, i) => dot.setAttribute('aria-current', i === this.index ? 'true' : 'false'));
    }

    /* ── Controles ──────────────────────────────────────────────────────────────────────────────────────────── */

    onClick(event) {
      var control = event.target.closest('[data-carousel-action], [data-carousel-dot]');

      if (!control || !this.contains(control)) return;

      var action = control.getAttribute('data-carousel-action');

      if (action === 'next') {
        this.next();
      } else if (action === 'previous') {
        this.previous();
      } else {
        this.select(this.dots.indexOf(control));
      }

      this.pause();
    }

    onPointerDown(event) {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      // Un segundo dedo (pellizcar para ampliar) no inicia otro arrastre.
      if (this.pointer) return;

      this.pointer = { id: event.pointerId, x: event.clientX, y: event.clientY, dragging: false };
    }

    onPointerMove(event) {
      var start = this.pointer;

      if (!start || event.pointerId !== start.id) return;

      var deltaX = event.clientX - start.x;
      var deltaY = event.clientY - start.y;

      if (!start.dragging) {
        // Hasta que el dedo se decide no se mueve nada. Si se va en vertical, el gesto es de la página y se suelta.
        if (Math.abs(deltaX) < DRAG_SLOP || Math.abs(deltaX) <= Math.abs(deltaY)) {
          if (Math.abs(deltaY) >= DRAG_SLOP) this.pointer = null;
          return;
        }

        start.dragging = true;
        this.clean();
        this.pause();

        // Con el ratón, el arrastre sigue aunque el cursor salga de la imagen. Se captura ya decidido el gesto y no
        // en pointerdown, o los botones del texto dejarían de recibir su clic.
        try {
          this.media.setPointerCapture(event.pointerId);
        } catch (error) {}
      }

      if (this.effect === 'slide') this.drag(deltaX);
    }

    /* Coloca las imágenes desplazadas lo que lleva arrastrado el dedo, sin animar. */
    drag(deltaX) {
      var count = this.slides.length;
      var direction = this.rtl ? -1 : 1;
      var width = this.media.clientWidth || 1;

      // Como mucho se arrastra una imagen entera: más allá ya no queda otra al lado que enseñar.
      deltaX = Math.max(-width, Math.min(width, deltaX));

      this.slides.forEach((slide, i) => {
        var offset = this.offsetOf(i);

        // Con dos imágenes la otra es a la vez la siguiente y la anterior: se coloca del lado hacia el que se arrastra.
        if (count === 2 && offset !== 0) offset = deltaX < 0 ? direction : -direction;

        slide.style.transition = 'none';
        slide.style.transform = 'translateX(calc(' + offset * direction * 100 + '% + ' + deltaX + 'px))';
        // apply() usa esta posición para saber si al soltar la imagen tiene que animarse o recolocarse sin cruzar.
        slide.dataset.offset = offset;
      });
    }

    onPointerUp(event) {
      var start = this.pointer;

      if (!start || event.pointerId !== start.id) return;

      this.pointer = null;

      var deltaX = event.clientX - start.x;
      var deltaY = event.clientY - start.y;

      // Solo cuenta como gesto lo que es claramente horizontal: lo vertical es la clienta desplazando la página.
      var swipe = event.type !== 'pointercancel' && Math.abs(deltaX) >= SWIPE_THRESHOLD && Math.abs(deltaX) > Math.abs(deltaY);

      if (!start.dragging && !swipe) return;

      // Un arrastre, llegue o no a pasar de imagen, no debe abrir el enlace de la foto.
      this.swiped = true;
      setTimeout(() => (this.swiped = false), 0);

      if (this.effect === 'slide') this.slides.forEach((slide) => (slide.style.transition = ''));

      if (!swipe) {
        // No llegó: las imágenes vuelven a su sitio con la misma transición de siempre.
        this.apply(false);
      } else if (deltaX * (this.rtl ? -1 : 1) < 0) {
        this.next();
      } else {
        this.previous();
      }

      this.pause();
    }

    onClickCapture(event) {
      if (!this.swiped) return;

      event.preventDefault();
      event.stopPropagation();
    }

    /* ── Avance automático ──────────────────────────────────────────────────────────────────────────────────── */

    setupAutoplay() {
      var seconds = parseFloat(this.getAttribute('interval'));

      if (!(seconds > 0)) return;

      this.delay = Math.max(1, seconds) * 1000;
      this.timer = this.resumeTimer = 0;
      this.onScreen = false;
      // Ratón encima, interacción reciente o bloque abierto en el editor.
      this.blocked = false;

      this.sync = this.sync.bind(this);
      this.pause = this.pause.bind(this);
      this.onPointerHover = this.onPointerHover.bind(this);
      this.onVideoEnded = this.onVideoEnded.bind(this);

      // Un vídeo del carrusel no se corta a los X segundos: se pasa de diapositiva cuando termina. Para enterarse hay
      // que quitarle el bucle (un vídeo en bucle nunca «termina»); sin avance automático lo conserva.
      this.videos = this.slides.filter((slide) => slide.tagName === 'VIDEO');
      this.videos.forEach((video) => {
        video.loop = false;
        video.addEventListener('ended', this.onVideoEnded);
      });

      REDUCED_MOTION.addEventListener('change', this.sync);
      document.addEventListener('visibilitychange', this.sync);
      PAUSE_EVENTS.forEach((type) => this.media.addEventListener(type, this.pause, { passive: true }));
      this.media.addEventListener('pointerenter', this.onPointerHover);
      this.media.addEventListener('pointerleave', this.onPointerHover);

      // observe() dispara una primera entrada con el estado actual, así que este observer hace también el arranque.
      this.observer = new IntersectionObserver((entries) => {
        this.onScreen = entries[entries.length - 1].isIntersecting;
        this.sync();
      });
      this.observer.observe(this.media);
    }

    teardownAutoplay() {
      if (!this.delay) return;

      this.stop();
      clearTimeout(this.resumeTimer);
      REDUCED_MOTION.removeEventListener('change', this.sync);
      document.removeEventListener('visibilitychange', this.sync);
      PAUSE_EVENTS.forEach((type) => this.media.removeEventListener(type, this.pause));
      this.media.removeEventListener('pointerenter', this.onPointerHover);
      this.media.removeEventListener('pointerleave', this.onPointerHover);
      this.videos.forEach((video) => video.removeEventListener('ended', this.onVideoEnded));
      this.observer.disconnect();
    }

    /* Única puerta de entrada: decide si el temporizador debe existir. */
    sync() {
      if (this.canAdvance()) {
        // Si la clienta lleva más de RESUME_DELAY con el dedo arrastrando, el avance espera a que suelte. Y si lo que se
        // ve es un vídeo en marcha, también: pasará solo cuando termine (onVideoEnded).
        if (!this.timer) this.timer = setInterval(() => !(this.pointer && this.pointer.dragging) && !this.videoRunning() && this.next(), this.delay);
      } else {
        this.stop();
      }
    }

    canAdvance() {
      return this.onScreen && !this.blocked && !REDUCED_MOTION.matches && !document.hidden;
    }

    stop() {
      this.timer = clearInterval(this.timer);
    }

    /* Un vídeo que no llega a reproducirse (menos animación, ahorro de datos, un error) no detiene nada: se queda en su
       cartel y pasa al tiempo de siempre, como una imagen. */
    videoRunning() {
      var slide = this.slides[this.index];

      return slide.tagName === 'VIDEO' && !slide.paused && !slide.ended;
    }

    onVideoEnded(event) {
      if (event.target !== this.slides[this.index]) return;

      if (this.canAdvance()) {
        this.next();
        // La cuenta de la siguiente diapositiva empieza ahora, no a mitad del intervalo que iba corriendo.
        this.stop();
        this.sync();
      } else {
        // Con el ratón encima o un bloque abierto en el editor el carrusel no se mueve: el vídeo vuelve a empezar en vez
        // de quedarse congelado en el último fotograma.
        event.target.currentTime = 0;
        var playing = event.target.play();
        if (playing && playing.catch) playing.catch(function () {});
      }
    }

    /* Cualquier gesto de la clienta manda: paramos y volvemos a intentarlo pasado RESUME_DELAY. */
    pause() {
      if (!this.delay) return;

      this.blocked = true;
      this.stop();
      clearTimeout(this.resumeTimer);
      this.resumeTimer = setTimeout(() => {
        this.blocked = false;
        this.sync();
      }, RESUME_DELAY);
    }

    /* Con el ratón encima el carrusel se queda quieto todo el rato, no solo unos segundos. */
    onPointerHover(event) {
      if (event.pointerType !== 'mouse') return;

      this.blocked = event.type === 'pointerenter';
      clearTimeout(this.resumeTimer);
      this.sync();
    }

    /* Editor: con un bloque de imagen abierto el carrusel enseña esa imagen y no se mueve hasta que se cierra. */
    onBlockToggle(event) {
      var selected = this.slides.findIndex((slide) => {
        var picture = slide.closest('[data-block-id]');

        return picture && picture.dataset.blockId === event.detail.blockId;
      });

      if (selected < 0) return;

      if (event.type === 'shopify:block:select') this.select(selected);

      if (!this.delay) return;

      this.blocked = event.type === 'shopify:block:select';
      clearTimeout(this.resumeTimer);
      this.sync();
    }
  }

  customElements.define('image-overlay-carousel', ImageOverlayCarousel);
})();
